import React from 'react';
import { shouldStartRecallBot } from '../../utils/meetingUtils';

interface MeetingActionBarProps {
  // Meeting data
  meeting?: {
    id?: number;
    team_url?: string;
    status?: string;
    // Both spellings: the calendar endpoints return re_intake, the patient page re_inatke
    re_intake?: boolean;
    re_inatke?: boolean;
  };

  // Meeting type ID to determine if it's a PSY meeting
  meetingTypeId?: number;

  // Action handlers (callbacks)
  onSave: () => Promise<void> | void;
  onFinish?: () => Promise<void> | void;
  onNoShow: () => void;
  onFinishTreatment?: () => void;
  onSendMeetingLink?: () => void;
  onRequestDocuments?: () => void;
  onCall?: () => void;

  // Loading states
  isSaving?: boolean;
  isFinishing?: boolean;

  // Button customization
  saveButtonText?: string;
  finishButtonText?: string;
  showFinishButton?: boolean;

  // Additional buttons to show in the center section
  additionalButtons?: React.ReactNode;

  // Optional right-side content
  rightContent?: React.ReactNode;

  // Custom styling
  className?: string;
}

export const MeetingActionBar: React.FC<MeetingActionBarProps> = ({
  meeting,
  meetingTypeId,
  onSave,
  onFinish,
  onNoShow,
  onFinishTreatment,
  onSendMeetingLink,
  onRequestDocuments,
  onCall,
  isSaving = false,
  isFinishing = false,
  saveButtonText = 'Save',
  finishButtonText = 'Finish & Summarize',
  showFinishButton = true,
  additionalButtons,
  rightContent,
  className = '',
}) => {
  const handleTeamLinkClick = async () => {
    if (!meeting?.team_url) return;

    // Open team URL in new tab
    window.open(meeting.team_url, '_blank');

    // For every video meeting (PSY 2/3, CM intake 1, CM re-intake 4), also call
    // start_meeting API so the transcription bot joins
    if (shouldStartRecallBot({ ...meeting, meetingtype_id: meetingTypeId }) && meeting.id) {
      try {
        const token = localStorage.getItem('token');
        const apiUrl = localStorage.getItem('apiUrl') || process.env.REACT_APP_API_URL || 'https://dev-backend.taliaz-cm.com';
        const url = `${apiUrl}/start_meeting?token=${token}&meeting_id=${meeting.id}`;

        await fetch(url, { method: 'GET' });
        console.log(`[PSY Meeting] Called start_meeting for meeting ${meeting.id}`);
      } catch (error) {
        console.error('[PSY Meeting] Failed to call start_meeting:', error);
        // Don't show error to user - this is a background call
      }
    }
  };

  const shouldShowFinish = showFinishButton && meeting?.status !== 'done' && onFinish;

  return (
    <div className={`flex items-center gap-4 mt-1 pt-2 pb-2 border-t border-gray-300 ${className}`}>
      {/* Left: Team Link and No Show Icons */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleTeamLinkClick}
          className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
          title="Team Link"
          disabled={!meeting?.team_url}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.658 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
        {meeting?.status !== 'noshow' && (
          <button
            onClick={onNoShow}
            className="p-2 text-red-600 hover:text-red-700 transition-colors text-xl"
            title="No Show"
          >
            ⛔
          </button>
        )}
        {onFinishTreatment && (
          <button
            onClick={onFinishTreatment}
            className="p-2 text-red-600 hover:text-red-700 transition-colors"
            title="Finish Treatment"
          >
            <svg className="w-6 h-6" fill="#e23232" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
            </svg>
          </button>
        )}
        {onSendMeetingLink && (
          <button
            onClick={onSendMeetingLink}
            className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
            title="Send Meeting Link"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="5" width="18" height="14" rx="2" ry="2" strokeWidth="2" />
              <path d="M3 7l9 6 9-6" strokeWidth="2" />
              <circle cx="17" cy="16" r="3" fill="currentColor" stroke="white" strokeWidth="1.5" />
              <path d="M15.5 16h3M17 14.5v3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
        {onRequestDocuments && (
          <button
            onClick={onRequestDocuments}
            className="p-2 text-orange-500 hover:text-orange-600 transition-colors"
            title="Request Documents"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
        )}
        {onCall && (
          <button
            onClick={onCall}
            className="p-2 text-green-600 hover:text-green-700 transition-colors"
            title="Call Patient"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex-1"></div>

      {/* Center: Save and Finish Buttons */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onSave}
          disabled={isSaving || isFinishing}
          className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : saveButtonText}
        </button>

        {shouldShowFinish && (
          <button
            onClick={onFinish}
            disabled={isSaving || isFinishing}
            className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFinishing ? 'Summarizing...' : finishButtonText}
          </button>
        )}

        {/* Additional buttons */}
        {additionalButtons}
      </div>

      <div className="flex-1"></div>

      {/* Right: Optional content */}
      {rightContent && (
        <div className="flex-shrink-0">
          {rightContent}
        </div>
      )}
    </div>
  );
};
